import React, { useState, useEffect, useCallback } from 'react';
import { useLazyQuery, useMutation } from '@apollo/client';
import { Card, Button, Input, Typography, Alert, Spin, Result, Tag, Divider, message } from 'antd';
import {
    CheckCircleOutlined,
    CloseCircleOutlined,
    SwapOutlined,
    LockOutlined,
    CalendarOutlined,
    TrophyOutlined,
    NumberOutlined,
    ShoppingOutlined,
} from '@ant-design/icons';
import { useParams } from 'react-router-dom';
import { useIntl } from 'react-intl';
import { PLAYER_BY_ENTRY_TOKEN } from '../../apollo/query/entry';
import { CONFIRM_PLAYER_ENTRY } from '../../apollo/mutation/entry';
import IntlMessages from '../../util/IntlMessages';
import { formatDateTime } from '../../util/date';

const { Title, Text } = Typography;

const PRIMARY = '#205173';
const SUCCESS = '#52c41a';
const LOGO = '/assets/images/keepsporting_logo.png';

const STORAGE_KEY = 'ks_entry_auth';

const pageStyle = {
    minHeight: '100vh',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    background: 'linear-gradient(160deg, #e8edf2 0%, #d5dde6 100%)',
    padding: '16px 12px',
};

const cardStyle = {
    width: '100%',
    maxWidth: 420,
    borderRadius: 16,
    overflow: 'hidden',
    boxShadow: '0 8px 32px rgba(0,0,0,0.10)',
    border: 'none',
};

const getStoredAuth = () => {
    try {
        const stored = localStorage.getItem(STORAGE_KEY);
        if (stored) return JSON.parse(stored);
    } catch { /* corrupted */ }
    return null;
};

const storeAuth = (pin, eventName, subEventName) => {
    try {
        localStorage.setItem(STORAGE_KEY, JSON.stringify({
            pin, event_name: eventName, sub_event_name: subEventName,
        }));
    } catch { /* unavailable */ }
};

const clearStoredAuth = () => {
    try { localStorage.removeItem(STORAGE_KEY); }
    catch { /* unavailable */ }
};

const LogoBanner = () => (
    <div style={{ padding: '20px 24px 12px', textAlign: 'center', background: '#fff' }}>
        <img src={LOGO} alt="KeepSporting" style={{ height: 32 }} />
    </div>
);

const DetailRow = ({ icon, label, children }) => {
    if (!children) return null;
    return (
        <div style={{ display: 'flex', alignItems: 'flex-start', padding: '10px 0', borderBottom: '1px solid #f5f5f5' }}>
            <div style={{ color: PRIMARY, marginRight: 12, marginTop: 3, minWidth: 16, fontSize: 14 }}>{icon}</div>
            <div style={{ flex: 1, minWidth: 0 }}>
                {label && <Text type="secondary" style={{ fontSize: 11, display: 'block', lineHeight: 1.2, marginBottom: 2 }}>{label}</Text>}
                <div style={{ fontSize: 14, fontWeight: 500, wordBreak: 'break-word' }}>{children}</div>
            </div>
        </div>
    );
};

const EntryPage = () => {
    const { token } = useParams();
    const intl = useIntl();

    const storedAuth = getStoredAuth();
    const [pin, setPin] = useState(storedAuth?.pin || '');
    const [pinSubmitted, setPinSubmitted] = useState(!!storedAuth?.pin);
    const [mutatedPlayer, setMutatedPlayer] = useState(null);

    const [fetchPlayer, { loading: queryLoading, error: queryError, data: queryData, called: queryCalled }] = useLazyQuery(PLAYER_BY_ENTRY_TOKEN, {
        fetchPolicy: 'no-cache',
        onCompleted: (data) => {
            if (data?.playerByEntryToken) {
                storeAuth(pin, data.playerByEntryToken.event_name, data.playerByEntryToken.sub_event_name);
            }
        },
    });

    const [confirmEntry, { loading: mutationLoading }] = useMutation(CONFIRM_PLAYER_ENTRY, {
        onCompleted: (data) => {
            if (data?.confirmPlayerEntry) {
                setMutatedPlayer(data.confirmPlayerEntry);
                message.success(intl.formatMessage({ id: 'access_management.entry_success' }));
            }
        },
        onError: (error) => {
            const gqlError = error?.graphQLErrors?.[0];
            const errorMessageKey = gqlError?.extensions?.response?.message || gqlError?.message || 'server_error.player_not_found';
            message.error(intl.formatMessage({ id: errorMessageKey }));
        },
    });

    // Player from mutation (after confirm) takes priority, otherwise from query
    const player = mutatedPlayer || queryData?.playerByEntryToken || null;

    useEffect(() => {
        if (storedAuth?.pin) {
            fetchPlayer({ variables: { token, pin: storedAuth.pin } });
        }
    }, [token]); // eslint-disable-line react-hooks/exhaustive-deps

    const handlePinSubmit = useCallback(() => {
        if (pin.length !== 6) {
            message.warning(intl.formatMessage({ id: 'access_management.invalid_pin' }));
            return;
        }
        setPinSubmitted(true);
        fetchPlayer({ variables: { token, pin } });
    }, [pin, token, fetchPlayer, intl]);

    const handleConfirmEntry = useCallback(() => {
        confirmEntry({ variables: { token, pin } });
    }, [token, pin, confirmEntry]);

    const handlePinChange = useCallback((e) => {
        const value = e.target.value.replace(/\D/g, '').slice(0, 6);
        setPin(value);
    }, []);

    const handleKeyDown = useCallback(
        (e) => { if (e.key === 'Enter') handlePinSubmit(); },
        [handlePinSubmit],
    );

    const handleResetPin = useCallback(() => {
        clearStoredAuth();
        setPinSubmitted(false);
        setMutatedPlayer(null);
        setPin('');
    }, []);

    // PIN entry screen
    if (!pinSubmitted) {
        return (
            <div style={pageStyle}>
                <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                    <LogoBanner />
                    <div style={{ padding: '24px 24px 28px', textAlign: 'center' }}>
                        <div style={{
                            width: 52, height: 52, borderRadius: '50%',
                            background: `${PRIMARY}12`, display: 'inline-flex',
                            alignItems: 'center', justifyContent: 'center', marginBottom: 12,
                        }}>
                            <LockOutlined style={{ fontSize: 22, color: PRIMARY }} />
                        </div>
                        <Title level={4} style={{ margin: '0 0 4px', color: PRIMARY }}>
                            <IntlMessages id="access_management.enter_pin" />
                        </Title>
                        <Text type="secondary" style={{ fontSize: 13 }}>
                            {storedAuth
                                ? storedAuth.event_name
                                : <IntlMessages id="access_management.enter_pin_subtitle" />
                            }
                        </Text>
                        <Input
                            size="large"
                            placeholder="------"
                            value={pin}
                            onChange={handlePinChange}
                            onKeyDown={handleKeyDown}
                            maxLength={6}
                            style={{
                                textAlign: 'center', fontSize: 28, letterSpacing: 12,
                                fontWeight: 600, marginTop: 20, marginBottom: 16,
                                borderRadius: 10, height: 56, borderColor: '#d9d9d9',
                            }}
                            inputMode="numeric"
                            pattern="[0-9]*"
                            autoFocus
                        />
                        <Button
                            type="primary" size="large" block
                            onClick={handlePinSubmit}
                            disabled={pin.length !== 6}
                            style={{
                                borderRadius: 10, height: 48, fontWeight: 600, fontSize: 15,
                                background: pin.length === 6 ? PRIMARY : undefined,
                                borderColor: pin.length === 6 ? PRIMARY : undefined,
                            }}
                        >
                            <IntlMessages id="access_management.verify_pin" />
                        </Button>
                    </div>
                </Card>
            </div>
        );
    }

    // Loading — also covers the brief frame between fetchPlayer() call and queryLoading becoming true
    if (queryLoading || (queryCalled && !queryData && !queryError)) {
        return <div style={pageStyle}><Spin size="large" /></div>;
    }

    // Error / not found
    if (queryError || !player) {
        const gqlError = queryError?.graphQLErrors?.[0];
        const errorMessageKey = gqlError?.extensions?.response?.message || gqlError?.message || 'server_error.player_not_found';
        return (
            <div style={pageStyle}>
                <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                    <LogoBanner />
                    <div style={{ padding: '8px 24px 28px' }}>
                        <Result
                            status="error"
                            icon={<CloseCircleOutlined style={{ color: '#ff4d4f' }} />}
                            title={intl.formatMessage({ id: errorMessageKey })}
                            extra={
                                <Button type="primary" onClick={handleResetPin}
                                    style={{ borderRadius: 10, background: PRIMARY, borderColor: PRIMARY }}>
                                    <IntlMessages id="access_management.change_pin" />
                                </Button>
                            }
                        />
                    </div>
                </Card>
            </div>
        );
    }

    // Not confirmed
    if (!player.confirmed) {
        return (
            <div style={pageStyle}>
                <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                    <LogoBanner />
                    <div style={{ padding: '8px 24px 28px' }}>
                        <Result status="warning" icon={<CloseCircleOutlined />}
                            title={intl.formatMessage({ id: 'access_management.not_confirmed' })} />
                    </div>
                </Card>
            </div>
        );
    }

    const alreadyEntered = !!player.entry_at;
    const headerBg = alreadyEntered
        ? `linear-gradient(135deg, ${SUCCESS} 0%, #389e0d 100%)`
        : `linear-gradient(135deg, ${PRIMARY} 0%, #153a52 100%)`;
    const subEventTypeLabel = player.event_type
        ? intl.formatMessage({ id: `common.event_type_${player.event_type}` })
        : null;

    return (
        <div style={pageStyle}>
            <Card style={cardStyle} bodyStyle={{ padding: 0 }}>
                {/* Logo */}
                <LogoBanner />

                {/* Colored header with name */}
                <div style={{
                    background: headerBg,
                    padding: '20px 24px 24px',
                    textAlign: 'center', color: '#fff',
                }}>
                    <div style={{
                        width: 56, height: 56, borderRadius: '50%',
                        background: 'rgba(255,255,255,0.2)',
                        display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                        marginBottom: 10,
                    }}>
                        {alreadyEntered
                            ? <CheckCircleOutlined style={{ fontSize: 28, color: '#fff' }} />
                            : <span style={{ fontSize: 22, fontWeight: 700, color: '#fff', textTransform: 'uppercase' }}>
                                {player.first_name?.[0]}{player.last_name?.[0]}
                            </span>
                        }
                    </div>

                    <Title level={4} style={{ color: '#fff', margin: '0 0 4px' }}>
                        {player.first_name} {player.last_name}
                    </Title>

                    {player.tracking_number && (
                        <Tag style={{
                            marginTop: 4, background: 'rgba(255,255,255,0.2)',
                            border: 'none', color: '#fff', fontSize: 15,
                            fontWeight: 600, padding: '2px 14px', borderRadius: 16,
                        }}>
                            #{player.tracking_number}
                        </Tag>
                    )}
                </div>

                {/* Details */}
                <div style={{ padding: '12px 24px 20px' }}>
                    <DetailRow icon={<CalendarOutlined />} label={intl.formatMessage({ id: 'common.event' })}>
                        {player.event_name}
                    </DetailRow>

                    {player.sub_event_name && (
                        <DetailRow icon={<TrophyOutlined />} label={subEventTypeLabel}>
                            {player.sub_event_name}
                        </DetailRow>
                    )}

                    {player.custom_fields?.filter((f) => f.value).map((field) => (
                        <DetailRow key={field.name} icon={<NumberOutlined />} label={field.label}>
                            {intl.messages[`common.${field.value}`]
                                ? intl.formatMessage({ id: `common.${field.value}` })
                                : field.value}
                        </DetailRow>
                    ))}

                    {player.product_variants?.map((variant) => (
                        <DetailRow key={variant.sku} icon={<ShoppingOutlined />} label={variant.product?.name}>
                            {(variant.attributes || []).map((attr, i) => (
                                <Tag key={i} color={PRIMARY} style={{ marginRight: 4, marginBottom: 2, borderRadius: 4 }}>
                                    {attr.option_name_locale}
                                </Tag>
                            ))}
                        </DetailRow>
                    ))}

                    <Divider style={{ margin: '14px 0 12px' }} />

                    {alreadyEntered ? (
                        <Alert
                            type="success"
                            showIcon
                            icon={<CheckCircleOutlined />}
                            message={
                                <Text strong style={{ color: '#389e0d' }}>
                                    {intl.formatMessage(
                                        { id: 'access_management.already_entered' },
                                        { date: formatDateTime(player.entry_at) },
                                    )}
                                </Text>
                            }
                            style={{ borderRadius: 10 }}
                        />
                    ) : (
                        <Button
                            type="primary" size="large" block
                            icon={<CheckCircleOutlined />}
                            loading={mutationLoading}
                            onClick={handleConfirmEntry}
                            style={{
                                borderRadius: 10, height: 50, fontWeight: 600, fontSize: 15,
                                background: PRIMARY, borderColor: PRIMARY,
                            }}
                        >
                            <IntlMessages id="access_management.confirm_entry" />
                        </Button>
                    )}

                    <Button
                        type="text" size="small" block
                        icon={<SwapOutlined />}
                        onClick={handleResetPin}
                        style={{ marginTop: 10, color: '#aaa', fontSize: 12 }}
                    >
                        <IntlMessages id="access_management.change_pin" />
                    </Button>
                </div>
            </Card>
        </div>
    );
};

export default EntryPage;
